import {
  isTooManyRequestsError,
  rateLimitRetryOptions,
  throwIfTooManyRequests,
  TOO_MANY_REQUESTS_STATUS,
  TooManyRequestsError,
} from '@restDataAccess/rateLimit';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import type { ApiNameType } from '@/constants';

import { authorizationHeader } from '../authorizationHeader.ts';
import { assertApiBaseForFetch } from './getApiUrl/getApiUrl.functions.ts';
import { useGetApiUrl } from './useGetApiUrl.ts';

type keyedMultiFetchProps = {
  baseApiRoute: ApiNameType;
  cacheKeys: (string | number | null | undefined)[];
  enabled?: boolean;
  headers?: HeadersInit;
  routes: Record<string, string>; // key -> route
  refetchOnWindowFocus?: boolean;
  refetchInterval?: number;
};

export const useAuthApiGetKeyedMultiFetch = <T>({
  baseApiRoute,
  cacheKeys,
  enabled = true,
  headers,
  routes,
  refetchOnWindowFocus = false,
  refetchInterval,
}: keyedMultiFetchProps) => {
  const auth = useAuth();

  const api = useGetApiUrl(baseApiRoute);

  const queryKey = cacheKeys.filter(
    (key): key is string | number => key !== null && key !== undefined
  );

  const getData = async (): Promise<Record<string, T>> => {
    const base = assertApiBaseForFetch(api);
    return await Promise.allSettled(
      Object.entries(routes).map(([key, route]) =>
        fetch(`${base}${route}`, {
          headers: {
            ...authorizationHeader(auth.user),
            ...headers,
          },
        }).then(async response => {
          if (response.ok) {
            return { key, result: await response.json() };
          }
          if (response.status === TOO_MANY_REQUESTS_STATUS) {
            throw new TooManyRequestsError();
          }
          const errorText = await response.text();
          console.error(`Failed to fetch: `, errorText);

          if (response.status === 401) {
            throw new Error(`Unauthorized: Please log in to continue.`);
          }

          throw new Error(`Failed to fetch: ${response.statusText}`);
        })
      )
    ).then(r => {
      // Rate limiting must fail the whole query so it can be retried; otherwise the partial
      // result would be cached as a success.
      throwIfTooManyRequests(r);
      return r
        .filter(
          (v: PromiseSettledResult<{ key: string; result: Awaited<T> }>) => v.status === 'fulfilled'
        )
        .reduce(
          (acc, v: PromiseFulfilledResult<{ key: string; result: T }>) => ({
            ...acc,
            [v.value.key]: v.value.result,
          }),
          {} as Record<string, T>
        );
    });
  };
  const query = useQuery<Record<string, T>>({
    ...rateLimitRetryOptions,
    queryKey: [...queryKey, api, routes],
    queryFn: getData,
    refetchOnWindowFocus,
    refetchInterval,
    enabled: enabled && api != null,
  });

  return { ...query, isRateLimited: isTooManyRequestsError(query.failureReason) };
};
